import React from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Card, ComponentLoader, Heading, Layout, MainLoader, Text, WizardFooter, WizardHeader } from '@netapp/bxp-design-system-react';
import { configurations as i18nNamespace } from '../../i18n/namespace';
import EmailServerConfigurationCard from './components/email-configuration/EmailServerCard';
import WebhookConfigurationCard from './components/webhook-configuration/WebhookConfigurationCard';
import { InfoIcon, SuccessIcon } from '@netapp/bxp-style/react-icons/Notification';
import styles from './configurations.module.scss';

const ErrorMessage = ({ message, dataTestId }) => {
    return <Text dataTestId={dataTestId} className={styles.marginAuto}>{message}</Text>
}

export const ConfigurationBaseHeader = ({ label, onClose }) => {
    return (
        <WizardHeader headerColor="text-primary" label={label} onClose={onClose} />
    );
}

export const ConfigurationBaseFooter = ({ children, style }) => {
    return (
        <WizardFooter style={style}>
            {children}
        </WizardFooter>
    );
}

export const ConfigurationBaseContent = ({ children, status, description, iconRenderer, style}) => {
    const Icon = iconRenderer || (() => null);

    return (
        <Layout.Content>
            <Layout.Container className={styles.baseContainer} style={style?.base}>
                <Layout.Grid centerContent lg={12} rowGap='md'>
                    <Layout.GridItem lg={8} md={8} sm={10}>
                        <Card className={styles.statusCard} style={style?.card}>
                            <Layout.Grid>
                                <Layout.GridItem lg={2} className={styles.iconGridItem}>
                                    <div className={styles.iconBaseContentWrapper}><Icon className={styles.icon} /></div>
                                </Layout.GridItem>
                                <Layout.GridItem lg={10} className={styles.statusGridItem}>
                                    <div className={styles.statusRow}>
                                        {_.toLower(status) === 'configured' ? <SuccessIcon /> : <InfoIcon />}
                                        <Text level={14} className={styles.statusText}>{status}</Text>
                                    </div>
                                    {description && <Text level={16}>{description}</Text>}
                                </Layout.GridItem>
                            </Layout.Grid>
                        </Card>
                        {children}
                    </Layout.GridItem>
                </Layout.Grid>
            </Layout.Container>
        </Layout.Content>
    );
}

export const ConfigurationBaseLayout = ({ children, isLoading }) => {
    return (
        <Layout.Page>
            {isLoading ? <MainLoader /> : children}
        </Layout.Page>
    )
}

export const ConfigurationBaseCard = ({ children, iconRenderer, title, hasNoPermission, hasError, isLoading, hasNoData, errorMessage, noPermissionMessage, noDataMessage }) => {

    const Icon = iconRenderer || (() => null);
    const { t } = useTranslation(i18nNamespace);

    const cardContent = React.useMemo(() => {
        if (hasNoPermission) {
            return <ErrorMessage dataTestId={`${title}-card-no-permission`} message={noPermissionMessage ?? t("no_card_permission")} />;
        } else if (isLoading) {
            return <ComponentLoader className={styles["margin-auto"]} />;
        } else if (hasError) {
            return <ErrorMessage dataTestId={`${title}-card-error`} message={errorMessage ?? t("card_load_error")} />;
        } else if (hasNoData) {
            return <ErrorMessage dataTestId={`${title}-card-no-data`} message={noDataMessage ?? t("no_card_data")} />;
        } else {
            return children;
        }
    }, [isLoading, hasError, hasNoPermission, hasNoData, children, t, errorMessage, noPermissionMessage, noDataMessage]);

    return (
        <Card className={styles.baseCard}>
            <Layout.GridItem lg={1} className={styles.iconGridItem}>
                <div className={styles.iconWrapper}><Icon className={styles.icon} /></div>
            </Layout.GridItem>
            <Layout.GridItem lg={11} className={styles.cardContentGridItem}>
                <Heading level={16} bold className={styles.cardTitle}>{title}</Heading>
                {cardContent}
            </Layout.GridItem>
        </Card>
    )
}

const Configurations = () => {
    const { t } = useTranslation(i18nNamespace);

    return (
        <Layout.Page>
            <Layout.Content>
                <Layout.Container style={{ padding: '40px' }}>
                    <Layout.Grid style={{ marginBottom: '24px' }}>
                        <Layout.GridItem lg={12}>
                            <Heading level={20}>{t('system_configuration')}</Heading>
                            <Text level={16}>{t('system_configuration_desc')}</Text>
                        </Layout.GridItem>
                    </Layout.Grid>
                    <Layout.Grid rowGap='md'>
                        <Layout.GridItem lg={6} md={6} sm={12}>
                            <EmailServerConfigurationCard />
                        </Layout.GridItem>
                        <Layout.GridItem lg={6} md={6} sm={12}>
                            <WebhookConfigurationCard />
                        </Layout.GridItem>
                    </Layout.Grid>
                </Layout.Container>
            </Layout.Content>
        </Layout.Page>
    )
}

export default React.memo(Configurations);